import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './RollenBeheer.css';

const MODULE_LABELS = {
  monteurs_app: '📱 Monteurs-app',
  brieven: '✉️ Brieven', forms: '📋 Forms', werkorders: '🔧 Werkorders',
  klanten: '👥 Klanten', planning: '📅 Planning', projecten: '📁 Projecten',
  materialen: '📦 Materialen', documenten: '📄 Documenten', onderhoud: '🛠️ Onderhoud',
  monteur_logs: '📝 Monteur Logs', toolboxen: '🧰 Toolboxen', uren: '⏱️ Uren',
  cursussen: '🎓 Cursussen', afdelingen: '🏢 Afdelingen',
  gebruikers: '🔐 Gebruikers & Rollen', templates: '📋 Templates',
  import: '📥 Import', kennisbank: '📚 Kennisbank', analyse: '📈 Analyse',
  instellingen: '⚙️ Instellingen', overig: '📌 Legacy',
};

const RollenBeheer = () => {
  const { gebruiker, hasPermission, fetchPermissions } = useAuth();
  const navigate = useNavigate();
  const [rollen, setRollen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ naam: '', beschrijving: '' });
  const [submitting, setSubmitting] = useState(false);

  const [expandedRolId, setExpandedRolId] = useState(null);
  const [rolRechten, setRolRechten] = useState({});
  const [selections, setSelections] = useState({});
  const [savingRol, setSavingRol] = useState(null);

  const magBeheren = gebruiker && hasPermission && hasPermission('gebruikers:rollen_beheer');

  const loadRollen = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/rollen');
      setRollen(res.data.rollen || []);
      setError(null);
    } catch (err) {
      setError('Kon rollen niet laden');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (gebruiker?.id && !magBeheren) { navigate('/'); return; }
    if (magBeheren) loadRollen();
  }, [gebruiker?.id, magBeheren, navigate, loadRollen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.naam.trim()) return;
    try {
      setSubmitting(true);
      await api.post('/rollen', {
        naam: formData.naam.trim(),
        beschrijving: formData.beschrijving.trim() || null,
      });
      setShowModal(false);
      setFormData({ naam: '', beschrijving: '' });
      await loadRollen();
    } catch (err) {
      alert(err.response?.data?.message || 'Fout bij aanmaken rol');
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (rol) => {
    if (!window.confirm(`Rol "${rol.naam}" verwijderen?`)) return;
    try { await api.delete(`/rollen/${rol.id}`); await loadRollen(); }
    catch (err) { alert(err.response?.data?.message || 'Fout bij verwijderen'); }
  };

  const toggleExpand = async (rolId) => {
    if (expandedRolId === rolId) { setExpandedRolId(null); return; }
    setExpandedRolId(rolId);
    if (!rolRechten[rolId]) {
      try {
        const res = await api.get(`/rechten/rol/${rolId}`);
        const rechten = res.data.rechten || [];
        setRolRechten(prev => ({ ...prev, [rolId]: rechten }));
        const ids = rechten.filter(r => r.heeft_recht).map(r => r.id);
        setSelections(prev => ({ ...prev, [rolId]: new Set(ids) }));
      } catch (err) {
        alert('Kon rechten niet laden: ' + (err.response?.data?.message || err.message));
      }
    }
  };

  const toggleRecht = (rolId, rechtId) => {
    setSelections(prev => {
      const cur = new Set(prev[rolId] || []);
      if (cur.has(rechtId)) cur.delete(rechtId); else cur.add(rechtId);
      return { ...prev, [rolId]: cur };
    });
  };

  const isDirty = (rolId) => {
    const orig = rolRechten[rolId];
    const cur = selections[rolId];
    if (!orig || !cur) return false;
    const origIds = new Set(orig.filter(r => r.heeft_recht).map(r => r.id));
    if (origIds.size !== cur.size) return true;
    for (const id of cur) if (!origIds.has(id)) return true;
    return false;
  };

  const saveRol = async (rolId) => {
    try {
      setSavingRol(rolId);
      const ids = Array.from(selections[rolId] || []);
      await api.post(`/rechten/rol/${rolId}`, { rechtenIds: ids });
      if (fetchPermissions) await fetchPermissions();
      setRolRechten(prev => ({
        ...prev,
        [rolId]: prev[rolId].map(r => ({ ...r, heeft_recht: ids.includes(r.id) }))
      }));
    } catch (err) {
      alert(err.response?.data?.message || 'Fout bij opslaan');
    } finally { setSavingRol(null); }
  };

  const groupRechten = (rechten) => {
    const groups = {};
    rechten.forEach(r => {
      const m = r.code.includes(':') ? r.code.split(':')[0] : 'overig';
      if (!groups[m]) groups[m] = [];
      groups[m].push(r);
    });
    return groups;
  };

  if (loading) return <div className="rb-page"><div className="rb-loading">Laden…</div></div>;
  const systemRollen = rollen.filter(r => r.is_system);
  const customRollen = rollen.filter(r => !r.is_system);

  const renderMatrix = (rol) => {
    const rechten = rolRechten[rol.id];
    if (!rechten) return <div className="rb-perm-loading">Rechten laden…</div>;
    const groups = groupRechten(rechten);
    const dirty = isDirty(rol.id);
    const aantal = (selections[rol.id] || new Set()).size;
    return (
      <div className="rb-perm-panel">
        <div className="rb-perm-summary">
          <strong>{aantal}</strong> van {rechten.length} rechten aangevinkt
        </div>
        <div className="rb-perm-modules">
          {Object.entries(groups).map(([module, items]) => (
            <div key={module} className="rb-perm-module">
              <div className="rb-perm-module-title">{MODULE_LABELS[module] || module}</div>
              <div className="rb-perm-checks">
                {items.map(r => (
                  <label key={r.id} className="rb-perm-check">
                    <input
                      type="checkbox"
                      checked={(selections[rol.id] || new Set()).has(r.id)}
                      onChange={() => toggleRecht(rol.id, r.id)}
                    />
                    <span>
                      <span className="rb-perm-name">{r.naam}</span>
                      {r.beschrijving && <div className="rb-perm-desc">{r.beschrijving}</div>}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="rb-perm-actions">
          {dirty && <span className="rb-perm-dirty">● Niet opgeslagen</span>}
          <button
            className="rb-btn rb-btn-primary"
            disabled={!dirty || savingRol === rol.id}
            onClick={() => saveRol(rol.id)}
          >
            {savingRol === rol.id ? 'Opslaan…' : 'Opslaan'}
          </button>
        </div>
      </div>
    );
  };

  const renderCard = (rol) => {
    const open = expandedRolId === rol.id;
    const custom = !rol.is_system;
    return (
      <div key={rol.id}>
        <div className={`rb-card ${custom ? 'rb-custom' : 'rb-system'} ${open ? 'rb-expanded' : ''}`}
             onClick={() => toggleExpand(rol.id)}>
          <div className="rb-card-body">
            <div className="rb-card-name">
              <span className="rb-chevron">{open ? '▼' : '▶'}</span> {rol.naam}
            </div>
            <div className="rb-card-desc">{rol.beschrijving || <em>geen beschrijving</em>}</div>
          </div>
          <div className="rb-actions" onClick={(e) => e.stopPropagation()}>
            {!custom && <div className="rb-badge rb-badge-system">SYSTEM</div>}
            {custom && (
              <button className="rb-btn rb-btn-danger" onClick={() => handleDelete(rol)}>Verwijderen</button>
            )}
          </div>
        </div>
        {open && renderMatrix(rol)}
      </div>
    );
  };

  return (
    <div className="rb-page">
      <header className="rb-header">
        <div>
          <h1>Rollen Beheer</h1>
          <p className="rb-sub">
            Klik op een rol om de rechten-matrix te openen. Vink aan/uit per module en klik Opslaan.
          </p>
        </div>
        <button className="rb-btn rb-btn-primary" onClick={() => { setFormData({ naam:'', beschrijving:'' }); setShowModal(true); }}>
          + Nieuwe rol
        </button>
      </header>

      {error && <div className="rb-error">{error}</div>}

      <section className="rb-section">
        <h2 className="rb-section-title">System-rollen ({systemRollen.length})</h2>
        <p className="rb-section-sub">Namen niet aanpasbaar — rechten per bedrijf wél.</p>
        <div className="rb-list">{systemRollen.map(renderCard)}</div>
      </section>

      <section className="rb-section">
        <h2 className="rb-section-title">Custom rollen ({customRollen.length})</h2>
        <p className="rb-section-sub">Eigen rollen voor jouw bedrijf — vrij te beheren.</p>
        {customRollen.length === 0 ? (
          <div className="rb-empty">Nog geen custom rollen. Klik op <strong>+ Nieuwe rol</strong>.</div>
        ) : (
          <div className="rb-list">{customRollen.map(renderCard)}</div>
        )}
      </section>

      {showModal && (
        <div className="rb-modal-overlay">
          <div className="rb-modal" style={{ position: "relative" }} onClick={(e) => e.stopPropagation()}>
            <h2>Nieuwe rol aanmaken</h2>
            <button onClick={() => setShowModal(false)} style={{ position: "absolute", top: 12, right: 14, background: "transparent", border: "none", fontSize: 24, lineHeight: 1, cursor: "pointer", color: "#94a3b8" }} title="Sluiten">×</button>
            <form onSubmit={handleSubmit}>
              <div className="rb-field">
                <label>Naam *</label>
                <input type="text" value={formData.naam}
                  onChange={(e) => setFormData({ ...formData, naam: e.target.value })}
                  placeholder="Bijv. Uitvoerder Senior" maxLength={50} autoFocus required />
              </div>
              <div className="rb-field">
                <label>Beschrijving</label>
                <textarea value={formData.beschrijving}
                  onChange={(e) => setFormData({ ...formData, beschrijving: e.target.value })}
                  placeholder="Wat doet deze rol?" rows={3} />
              </div>
              <div className="rb-modal-actions">
                <button type="button" className="rb-btn" onClick={() => setShowModal(false)} disabled={submitting}>
                  Annuleren
                </button>
                <button type="submit" className="rb-btn rb-btn-primary" disabled={submitting || !formData.naam.trim()}>
                  {submitting ? 'Bezig…' : 'Aanmaken'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RollenBeheer;
